<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  patchMarketplaceJson,
  postMarketplaceJsonBody,
  postMarketplaceForm,
} from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;

// ── Durum ────────────────────────────────────────────────────────────────
const loading = ref(true);
const error = ref('');
const assets = ref([]);
const total = ref(0);
const folders = ref([]);
const kinds = ref({});
const smartViews = reactive({});
const detective = reactive({});

const q = ref('');
const activeView = ref('all'); // 'all' | system_key | folderId | 'unused'|'duplicates'|'errors'|'large'|'archive'|'trash'
const activeKind = ref('all');
const sort = ref('newest');
const page = ref(1);

const selected = ref(new Set());
const drawer = reactive({
  open: false,
  loading: false,
  asset: null,
  usage: null,
  similar: [],
});
const uploading = ref(false);
const backfilling = ref(false);

// ── Yardımcılar ──────────────────────────────────────────────────────────
const KIND_ICON = {
  image: 'i-lucide-image',
  video: 'i-lucide-play',
  video_link: 'i-lucide-video',
  pdf: 'i-lucide-file-text',
  spreadsheet: 'i-lucide-sheet',
  document: 'i-lucide-file',
  other: 'i-lucide-file-question',
};
const FOLDER_ICON = {
  product_images: 'i-lucide-shirt',
  banners: 'i-lucide-flag',
  videos: 'i-lucide-clapperboard',
  brand: 'i-lucide-badge',
  supplier: 'i-lucide-package',
  documents: 'i-lucide-file-text',
  imports: 'i-lucide-download',
  archive: 'i-lucide-archive',
  trash: 'i-lucide-trash-2',
};

const humanSize = b => {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};
const isImage = a => a.kind === 'image';
const healthTone = s => {
  if (s == null) return 'bg-slate-300';
  if (s >= 80) return 'bg-green-500';
  if (s >= 55) return 'bg-yellow-500';
  return 'bg-red-500';
};

const kindChips = computed(() => [
  { key: 'all', label: 'Tümü', n: smartViews.all || 0 },
  { key: 'image', label: 'Görsel', n: kinds.value.image || 0 },
  {
    key: 'video',
    label: 'Video',
    n: (kinds.value.video || 0) + (kinds.value.video_link || 0),
  },
  { key: 'pdf', label: 'PDF', n: kinds.value.pdf || 0 },
  { key: 'excel', label: 'Excel/CSV', n: kinds.value.spreadsheet || 0 },
]);

const systemFolders = computed(() =>
  folders.value.filter(f => !['archive', 'trash'].includes(f.systemKey))
);
const archiveFolders = computed(() =>
  folders.value.filter(f => ['archive', 'trash'].includes(f.systemKey))
);

// ── Veri çekme ───────────────────────────────────────────────────────────
async function load() {
  loading.value = true;
  error.value = '';
  try {
    const params = new URLSearchParams();
    if (q.value.trim()) params.set('q', q.value.trim());
    if (activeKind.value !== 'all') params.set('kind', activeKind.value);
    if (activeView.value !== 'all') params.set('folder', activeView.value);
    params.set('sort', sort.value);
    params.set('page', page.value);
    const data = await fetchMarketplaceJson(
      accountId,
      `media_center?${params}`
    );
    assets.value = data.assets;
    total.value = data.total;
    folders.value = data.folders;
    kinds.value = data.kinds || {};
    Object.assign(smartViews, data.smartViews || {});
    Object.assign(detective, data.detective || {});
  } catch (e) {
    error.value =
      'Medya yüklenemedi. Dedektifi/backfill’i çalıştırmayı deneyin.';
  } finally {
    loading.value = false;
  }
}

let searchTimer;
watch(q, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    page.value = 1;
    load();
  }, 300);
});

function setView(v) {
  activeView.value = v;
  page.value = 1;
  selected.value = new Set();
  load();
}
function setKind(k) {
  activeKind.value = k;
  page.value = 1;
  load();
}

// ── Seçim ────────────────────────────────────────────────────────────────
function toggleSelect(id, e) {
  e.stopPropagation();
  const s = new Set(selected.value);
  if (s.has(id)) s.delete(id);
  else s.add(id);
  selected.value = s;
}
function clearSelection() {
  selected.value = new Set();
}

// ── Detay çekmecesi + Kullanım Haritası ──────────────────────────────────
async function openAsset(id) {
  drawer.open = true;
  drawer.loading = true;
  drawer.asset = null;
  try {
    const data = await fetchMarketplaceJson(accountId, `media_center/${id}`);
    drawer.asset = data.asset;
    drawer.usage = data.usage;
    drawer.similar = data.similar || [];
  } finally {
    drawer.loading = false;
  }
}
function closeDrawer() {
  drawer.open = false;
}
async function saveAsset() {
  const a = drawer.asset;
  const updated = await patchMarketplaceJson(
    accountId,
    `media_center/${a.id}`,
    {
      asset: {
        title: a.title,
        alt_text: a.altText,
        visibility: a.visibility,
        folder_id: a.folderId,
        tags: a.tags,
      },
    }
  );
  drawer.asset = { ...drawer.asset, ...updated.asset };
  load();
}
function addTag(e) {
  const v = e.target.value.trim();
  if (v && !drawer.asset.tags.includes(v)) drawer.asset.tags.push(v);
  e.target.value = '';
  saveAsset();
}
function removeTag(t) {
  drawer.asset.tags = drawer.asset.tags.filter(x => x !== t);
  saveAsset();
}

// ── Toplu işlem ──────────────────────────────────────────────────────────
async function bulk(op, extra = {}) {
  const ids = [...selected.value];
  if (!ids.length) return;
  if (
    op === 'delete' &&
    !window.confirm(`${ids.length} dosya kalıcı silinsin mi?`)
  )
    return;
  await postMarketplaceJsonBody(accountId, 'media_center/bulk', {
    ids,
    op,
    ...extra,
  });
  clearSelection();
  load();
}

// ── Yükleme + Backfill ───────────────────────────────────────────────────
async function onUpload(e) {
  const files = [...e.target.files];
  if (!files.length) return;
  uploading.value = true;
  try {
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      const folder = /^\d+$/.test(String(activeView.value))
        ? activeView.value
        : '';
      if (folder) fd.append('folderId', folder);
      // eslint-disable-next-line no-await-in-loop
      await postMarketplaceForm(accountId, 'media_center/upload', fd);
    }
    load();
  } finally {
    uploading.value = false;
    e.target.value = '';
  }
}
async function runBackfill() {
  backfilling.value = true;
  try {
    await postMarketplaceJsonBody(accountId, 'media_center/backfill', {});
    load();
  } finally {
    backfilling.value = false;
  }
}

onMounted(load);
</script>

<template>
  <div
    class="flex h-full flex-1 min-w-0 overflow-hidden bg-slate-50 dark:bg-slate-900"
  >
    <!-- ══ SOL RAY ══ -->
    <aside
      class="w-60 shrink-0 flex flex-col gap-5 overflow-y-auto border-r border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-4"
    >
      <div class="flex items-center gap-2.5 px-1.5">
        <div
          class="grid size-9 place-items-center rounded-lg bg-woot-500 text-white text-lg shadow-sm"
        >
          <span class="i-lucide-images size-5" />
        </div>
        <div>
          <h1 class="text-sm font-bold text-slate-900 dark:text-slate-100">
            Medya Merkezi
          </h1>
          <span class="text-[11px] text-slate-400">{{ (smartViews.all || 0) + (smartViews.archive || 0) }} dosya</span>
        </div>
      </div>

      <nav class="flex flex-col gap-0.5">
        <button
          class="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] font-medium"
          :class="
            activeView === 'all'
              ? 'bg-woot-50 text-woot-700 dark:bg-woot-800/40 dark:text-woot-200'
              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
          "
          @click="setView('all')"
        >
          <span class="i-lucide-layout-grid size-[18px] text-slate-400" />
          Tüm Dosyalar
          <span class="ml-auto font-mono text-xs text-slate-400">{{
            smartViews.all || 0
          }}</span>
        </button>
        <button
          v-for="f in systemFolders"
          :key="f.id"
          class="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] font-medium"
          :class="
            String(activeView) === String(f.id)
              ? 'bg-woot-50 text-woot-700 dark:bg-woot-800/40 dark:text-woot-200'
              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
          "
          @click="setView(f.id)"
        >
          <span
            class="size-[18px] text-slate-400"
            :class="FOLDER_ICON[f.systemKey] || 'i-lucide-folder'"
          />
          {{ f.name }}
          <span class="ml-auto font-mono text-xs text-slate-400">{{
            f.count
          }}</span>
        </button>
      </nav>

      <div class="border-t border-slate-100 dark:border-slate-800" />
      <nav class="flex flex-col gap-0.5">
        <div
          class="px-2.5 pb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400"
        >
          Akıllı Görünümler
        </div>
        <button
          v-for="v in [
            {
              k: 'unused',
              i: 'i-lucide-moon',
              label: 'Kullanılmayanlar',
              n: smartViews.unused,
            },
            {
              k: 'duplicates',
              i: 'i-lucide-copy',
              label: 'Tekrarlananlar',
              n: smartViews.duplicates,
            },
            {
              k: 'errors',
              i: 'i-lucide-triangle-alert',
              label: 'Hatalı Dosyalar',
              n: smartViews.errors,
            },
            {
              k: 'large',
              i: 'i-lucide-maximize-2',
              label: 'Büyük Boyutlu',
              n: smartViews.large,
            },
          ]"
          :key="v.k"
          class="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] font-medium"
          :class="
            activeView === v.k
              ? 'bg-woot-50 text-woot-700 dark:bg-woot-800/40 dark:text-woot-200'
              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
          "
          @click="setView(v.k)"
        >
          <span class="size-[18px] text-slate-400" :class="[v.i]" />
          {{ v.label }}
          <span class="ml-auto font-mono text-xs text-yellow-600">{{
            v.n || 0
          }}</span>
        </button>
      </nav>

      <div class="border-t border-slate-100 dark:border-slate-800" />
      <nav class="flex flex-col gap-0.5">
        <button
          v-for="f in archiveFolders"
          :key="f.id"
          class="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] font-medium"
          :class="
            activeView === f.systemKey
              ? 'bg-woot-50 text-woot-700 dark:bg-woot-800/40 dark:text-woot-200'
              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
          "
          @click="setView(f.systemKey)"
        >
          <span
            class="size-[18px]"
            :class="[
              FOLDER_ICON[f.systemKey],
              f.systemKey === 'trash' ? 'text-red-400' : 'text-slate-400',
            ]"
          />
          {{ f.name }}
          <span class="ml-auto font-mono text-xs text-slate-400">{{
            f.count
          }}</span>
        </button>
      </nav>
    </aside>

    <!-- ══ ORTA ══ -->
    <main class="relative flex flex-1 flex-col min-w-0 overflow-hidden">
      <!-- Üst bar -->
      <div
        class="flex items-center gap-3 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 py-3.5 shrink-0"
      >
        <div
          class="flex flex-1 min-w-0 items-center gap-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3.5 py-2.5 focus-within:bg-white dark:focus-within:bg-slate-800 focus-within:ring-2 focus-within:ring-woot-500"
        >
          <span class="i-lucide-search size-4 shrink-0 text-slate-400" />
          <input
            v-model="q"
            placeholder="Dosya adı, ürün kodu, SKU, barkod, renk, kategori, etiket…"
            class="flex-1 min-w-0 !m-0 !p-0 !h-auto text-sm !bg-transparent !border-0 text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-0"
          />
        </div>
        <label
          class="flex cursor-pointer items-center gap-2 rounded-lg bg-woot-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-woot-600"
        >
          <span
            :class="
              uploading ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-upload'
            "
            class="size-4"
          />
          Yükle
          <input
            type="file"
            multiple
            class="hidden"
            accept="image/*,video/*,.pdf,.xls,.xlsx,.csv"
            @change="onUpload"
          />
        </label>
      </div>

      <div class="flex-1 overflow-auto">
        <!-- Akıllı Medya Dedektifi -->
        <section
          class="mx-5 mt-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4"
        >
          <div class="mb-3 flex items-center gap-2.5">
            <span
              class="grid size-6 place-items-center rounded-md bg-violet-100 text-violet-600 dark:bg-violet-900/40"
            >
              <span class="i-lucide-search-check size-4" />
            </span>
            <b class="text-sm text-slate-800 dark:text-slate-100">Akıllı Medya Dedektifi</b>
            <span class="ml-auto text-xs text-slate-400">Medya Sağlığı %{{ detective.health ?? 100 }}</span>
          </div>
          <div class="flex flex-wrap gap-2.5">
            <div
              v-for="f in [
                {
                  n: detective.unused,
                  label: 'görsel hiçbir üründe kullanılmıyor',
                  tone: 'text-yellow-600',
                  view: 'unused',
                },
                {
                  n: detective.missingAlt,
                  label: 'görselde alt metin eksik',
                  tone: 'text-yellow-600',
                  view: 'errors',
                },
                {
                  n: detective.coverMissing,
                  label: 'ürünün kapak görseli yok',
                  tone: 'text-red-600',
                  view: null,
                },
                {
                  n: detective.large,
                  label: 'büyük görsel siteyi yavaşlatıyor',
                  tone: 'text-red-600',
                  view: 'large',
                },
                {
                  n: detective.duplicates,
                  label: 'dosya tekrar yüklenmiş',
                  tone: 'text-green-600',
                  view: 'duplicates',
                },
              ]"
              :key="f.label"
              class="flex items-center gap-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2"
            >
              <span class="font-mono text-[15px] font-bold" :class="f.tone">{{
                f.n ?? 0
              }}</span>
              <span
                class="max-w-[150px] text-[12.5px] leading-tight text-slate-500 dark:text-slate-400"
                >{{ f.label }}</span>
              <button
                v-if="f.view"
                class="rounded-md bg-woot-50 px-2 py-1 text-[11.5px] font-semibold text-woot-600 hover:bg-woot-100 dark:bg-woot-900/30"
                @click="setView(f.view)"
              >
                Göster
              </button>
            </div>
          </div>
        </section>

        <!-- Tip filtre çipleri -->
        <div class="flex flex-wrap items-center gap-2 px-5 pt-4 pb-1.5">
          <button
            v-for="c in kindChips"
            :key="c.key"
            class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] font-semibold"
            :class="
              activeKind === c.key
                ? 'border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900'
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-300 hover:border-slate-400'
            "
            @click="setKind(c.key)"
          >
            {{ c.label }}
            <span class="font-mono opacity-60">{{ c.n }}</span>
          </button>
          <div class="flex-1" />
          <select
            v-model="sort"
            class="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-[12.5px] text-slate-600 dark:text-slate-300"
            @change="load"
          >
            <option value="newest">Son yüklenen</option>
            <option value="oldest">En eski</option>
            <option value="largest">En büyük</option>
            <option value="name">Ada göre</option>
          </select>
        </div>

        <!-- Durumlar -->
        <div
          v-if="loading"
          class="flex h-48 items-center justify-center gap-2 text-slate-400"
        >
          <span class="i-lucide-loader-2 size-5 animate-spin" />
          <span class="text-sm">Yükleniyor…</span>
        </div>
        <div
          v-else-if="error"
          class="m-5 flex items-center gap-3 rounded-xl bg-red-50 p-4 text-sm text-red-600 dark:bg-red-950 dark:text-red-400"
        >
          <span class="i-lucide-alert-circle size-5 shrink-0" />
          <span class="flex-1">{{ error }}</span>
          <button
            class="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-red-600 dark:bg-slate-800"
            :disabled="backfilling"
            @click="runBackfill"
          >
            {{ backfilling ? 'Taranıyor…' : 'Dosyaları içe al' }}
          </button>
        </div>
        <div
          v-else-if="!assets.length"
          class="flex h-64 flex-col items-center justify-center gap-3 text-slate-400"
        >
          <span class="i-lucide-image-off size-10" />
          <p class="text-sm">Bu görünümde dosya yok.</p>
          <button
            class="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 dark:border-slate-700"
            :disabled="backfilling"
            @click="runBackfill"
          >
            {{ backfilling ? 'Taranıyor…' : 'Mevcut görselleri içe al' }}
          </button>
        </div>

        <!-- Izgara -->
        <div
          v-else
          class="grid gap-3.5 px-5 pb-24 pt-2"
          style="grid-template-columns: repeat(auto-fill, minmax(184px, 1fr))"
        >
          <div
            v-for="a in assets"
            :key="a.id"
            class="group relative cursor-pointer overflow-hidden rounded-xl border bg-white dark:bg-slate-800 transition hover:-translate-y-0.5 hover:shadow-lg"
            :class="
              selected.has(a.id)
                ? 'border-woot-500 ring-2 ring-woot-500'
                : 'border-slate-200 dark:border-slate-700 hover:border-woot-400'
            "
            @click="openAsset(a.id)"
          >
            <div
              class="relative grid aspect-square place-items-center bg-slate-100 dark:bg-slate-900"
            >
              <img
                v-if="isImage(a) && a.url"
                :src="a.url"
                :alt="a.altText || a.filename"
                loading="lazy"
                class="h-full w-full object-cover"
              />
              <span
                v-else
                class="size-9 text-slate-300"
                :class="KIND_ICON[a.kind] || 'i-lucide-file'"
              />
              <span
                class="absolute left-2 top-2 rounded bg-slate-900/70 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase text-white"
                >{{ a.format || a.kind }}</span>
              <button
                class="absolute right-1.5 top-1.5 grid size-5 place-items-center rounded-md border-2 border-white text-white"
                :class="selected.has(a.id) ? 'bg-woot-500' : 'bg-white/40'"
                @click="toggleSelect(a.id, $event)"
              >
                <span v-if="selected.has(a.id)" class="i-lucide-check size-3" />
              </button>
            </div>
            <div
              class="border-t border-slate-100 dark:border-slate-700 px-2.5 py-2.5"
            >
              <div
                class="truncate font-mono text-[11.5px] text-slate-600 dark:text-slate-300"
              >
                {{ a.filename }}
              </div>
              <div class="mt-1.5 flex items-center gap-1.5">
                <span
                  v-if="a.usageCount"
                  class="inline-flex items-center rounded-full bg-green-100 px-1.5 py-0.5 text-[10.5px] font-bold text-green-700 dark:bg-green-900/40"
                  >{{ a.usageCount }} yerde</span>
                <span
                  v-else
                  class="inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[10.5px] font-bold text-slate-400 dark:bg-slate-700"
                  >kullanılmıyor</span>
                <span
                  class="size-1.5 rounded-full"
                  :class="healthTone(a.healthScore)"
                />
                <span class="ml-auto font-mono text-[10.5px] text-slate-400">{{
                  humanSize(a.byteSize)
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Toplu seçim barı -->
      <div
        v-if="selected.size"
        class="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-white shadow-2xl"
      >
        <b class="font-mono">{{ selected.size }}</b> dosya seçili
        <div class="ml-2 flex flex-wrap gap-1.5">
          <button
            class="rounded-lg border border-white/20 bg-white/10 px-2.5 py-1.5 text-[12.5px] font-semibold hover:bg-white/20"
            @click="bulk('archive')"
          >
            Arşivle
          </button>
          <button
            class="rounded-lg border border-white/20 bg-white/10 px-2.5 py-1.5 text-[12.5px] font-semibold hover:bg-white/20"
            @click="bulk('trash')"
          >
            Çöpe at
          </button>
          <button
            v-if="activeView === 'trash' || activeView === 'archive'"
            class="rounded-lg border border-white/20 bg-white/10 px-2.5 py-1.5 text-[12.5px] font-semibold hover:bg-white/20"
            @click="bulk('restore')"
          >
            Geri al
          </button>
          <button
            class="rounded-lg border border-red-400/40 bg-white/10 px-2.5 py-1.5 text-[12.5px] font-semibold text-red-200 hover:bg-white/20"
            @click="bulk('delete')"
          >
            Sil
          </button>
          <button
            class="rounded-lg px-2 py-1.5 text-[12.5px] text-slate-300 hover:text-white"
            @click="clearSelection"
          >
            Vazgeç
          </button>
        </div>
      </div>
    </main>

    <!-- ══ SAĞ ÇEKMECE — Kullanım Haritası ══ -->
    <aside
      v-if="drawer.open"
      class="w-96 shrink-0 flex flex-col overflow-y-auto border-l border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900"
    >
      <div
        v-if="drawer.loading"
        class="flex h-48 items-center justify-center text-slate-400"
      >
        <span class="i-lucide-loader-2 size-5 animate-spin" />
      </div>
      <template v-else-if="drawer.asset">
        <div
          class="relative grid aspect-[4/3] place-items-center bg-slate-100 dark:bg-slate-800"
        >
          <img
            v-if="isImage(drawer.asset) && drawer.asset.url"
            :src="drawer.asset.url"
            class="h-full w-full object-contain"
          />
          <span
            v-else
            class="size-12 text-slate-300"
            :class="[KIND_ICON[drawer.asset.kind]]"
          />
          <button
            class="absolute right-3 top-3 grid size-8 place-items-center rounded-lg border border-slate-200 bg-white/90 text-slate-600 dark:bg-slate-900/90"
            @click="closeDrawer"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <div class="flex flex-col gap-5 p-4">
          <div>
            <input
              v-model="drawer.asset.title"
              placeholder="Başlık ekle…"
              class="w-full border-none bg-transparent text-[15px] font-bold text-slate-900 dark:text-slate-100 focus:outline-none"
              @blur="saveAsset"
            />
            <div class="mt-0.5 font-mono text-[11.5px] text-slate-400">
              {{ drawer.asset.filename }}
            </div>
          </div>

          <!-- Sağlık -->
          <div
            class="flex items-center gap-3 rounded-lg p-3"
            :class="
              drawer.asset.healthScore >= 80
                ? 'bg-green-50 dark:bg-green-900/20'
                : 'bg-yellow-50 dark:bg-yellow-900/20'
            "
          >
            <div
              class="font-mono text-2xl font-bold"
              :class="
                drawer.asset.healthScore >= 80
                  ? 'text-green-600'
                  : 'text-yellow-600'
              "
            >
              {{ drawer.asset.healthScore ?? '—' }}
            </div>
            <div class="flex-1">
              <div
                class="h-1.5 overflow-hidden rounded bg-white dark:bg-slate-800"
              >
                <div
                  class="h-full rounded"
                  :class="
                    drawer.asset.healthScore >= 80
                      ? 'bg-green-500'
                      : 'bg-yellow-500'
                  "
                  :style="{ width: (drawer.asset.healthScore ?? 100) + '%' }"
                />
              </div>
              <div class="mt-1.5 text-[11.5px] text-slate-500">
                <template v-if="drawer.asset.healthFlags?.length">
                  {{
                    drawer.asset.healthFlags
                      .map(
                        f =>
                          ({
                            no_alt_text: 'alt metin yok',
                            unused: 'kullanılmıyor',
                            too_large: 'çok büyük',
                            tiny: 'düşük çözünürlük',
                            no_webp: 'WebP yok',
                          })[f] || f
                      )
                      .join(' · ')
                  }}
                </template>
                <template v-else>Sağlıklı</template>
              </div>
            </div>
          </div>

          <!-- Dosya bilgisi -->
          <div>
            <div
              class="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400"
            >
              Dosya Bilgisi
            </div>
            <dl
              class="grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-1.5 text-[12.5px]"
            >
              <dt class="text-slate-400">Tür</dt>
              <dd
                class="text-right font-mono text-slate-600 dark:text-slate-300"
              >
                {{ drawer.asset.kind }} ·
                {{ (drawer.asset.format || '').toUpperCase() }}
              </dd>
              <dt class="text-slate-400">Boyut</dt>
              <dd
                class="text-right font-mono text-slate-600 dark:text-slate-300"
              >
                {{ humanSize(drawer.asset.byteSize) }}
              </dd>
              <dt v-if="drawer.asset.width" class="text-slate-400">
                Çözünürlük
              </dt>
              <dd
                v-if="drawer.asset.width"
                class="text-right font-mono text-slate-600 dark:text-slate-300"
              >
                {{ drawer.asset.width }} × {{ drawer.asset.height }}
              </dd>
              <dt class="text-slate-400">Kaynak</dt>
              <dd
                class="text-right font-mono text-slate-600 dark:text-slate-300"
              >
                {{ drawer.asset.source }}
              </dd>
              <dt class="text-slate-400">Görünürlük</dt>
              <dd class="text-right">
                <select
                  v-model="drawer.asset.visibility"
                  class="rounded border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[11px] text-slate-600 dark:border-slate-700 dark:bg-slate-800"
                  @change="saveAsset"
                >
                  <option value="public">Herkese açık</option>
                  <option value="private">Özel</option>
                  <option value="internal">Yalnızca iç</option>
                  <option value="channel_only">Kanala özel</option>
                </select>
              </dd>
            </dl>
          </div>

          <!-- Alt metin -->
          <div>
            <div
              class="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400"
            >
              Alt Metin (SEO)
            </div>
            <input
              v-model="drawer.asset.altText"
              placeholder="Görseli tarif eden metin…"
              class="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-[12.5px] text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-woot-500"
              @blur="saveAsset"
            />
          </div>

          <!-- Etiketler -->
          <div>
            <div
              class="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400"
            >
              Etiketler
            </div>
            <div class="flex flex-wrap items-center gap-1.5">
              <span
                v-for="t in drawer.asset.tags"
                :key="t"
                class="inline-flex items-center gap-1 rounded-md bg-woot-50 px-2 py-1 text-[11.5px] font-semibold text-woot-700 dark:bg-woot-900/30"
              >
                {{ t }}
                <button
                  class="i-lucide-x size-3 opacity-60 hover:opacity-100"
                  @click="removeTag(t)"
                />
              </span>
              <input
                placeholder="+ ekle"
                class="w-20 rounded-md border border-dashed border-slate-300 bg-transparent px-2 py-1 text-[11.5px] text-slate-500 focus:outline-none dark:border-slate-600"
                @keydown.enter.prevent="addTag"
              />
            </div>
          </div>

          <!-- KULLANIM HARİTASI -->
          <div>
            <div class="mb-2.5 flex items-center gap-2">
              <span
                class="text-[11px] font-bold uppercase tracking-wide text-slate-400"
                >Kullanım Haritası</span>
              <span
                class="rounded-full bg-slate-900 px-2 py-0.5 font-mono text-[11px] font-bold text-white dark:bg-slate-100 dark:text-slate-900"
                >{{ drawer.usage?.count || 0 }} yerde</span>
            </div>
            <div v-if="drawer.usage?.items?.length" class="flex flex-col gap-2">
              <div
                v-for="it in drawer.usage.items"
                :key="it.type + it.id"
                class="flex items-start gap-2.5 rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"
              >
                <div
                  class="grid size-10 shrink-0 place-items-center overflow-hidden rounded-lg bg-slate-100 dark:bg-slate-800"
                >
                  <img
                    v-if="it.image"
                    :src="it.image"
                    class="h-full w-full object-cover"
                  />
                  <span v-else class="i-lucide-shirt size-4 text-slate-400" />
                </div>
                <div class="min-w-0 flex-1">
                  <div
                    class="text-[13px] font-semibold leading-tight text-slate-800 dark:text-slate-100"
                  >
                    {{ it.name }}
                  </div>
                  <div
                    class="mt-0.5 flex flex-wrap items-center gap-1 text-[11.5px] text-slate-400"
                  >
                    <span>{{ it.subtitle }}</span>
                    <span
                      v-for="r in it.roles"
                      :key="r"
                      class="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500 dark:bg-slate-700"
                      >{{
                        {
                          cover: 'Kapak',
                          gallery: 'Galeri',
                          variant: 'Varyant',
                          banner: 'Banner',
                          creative: 'Kreatif',
                        }[r] || r
                      }}</span>
                  </div>
                  <div
                    v-if="it.variants?.length"
                    class="mt-1.5 flex flex-wrap gap-1"
                  >
                    <span
                      v-for="vv in it.variants"
                      :key="vv"
                      class="rounded border border-slate-200 px-1.5 font-mono text-[10.5px] font-semibold text-slate-600 dark:border-slate-700"
                      >{{ vv }}</span>
                  </div>
                </div>
              </div>
            </div>
            <p
              v-else
              class="rounded-lg bg-slate-50 dark:bg-slate-800 px-3 py-4 text-center text-[12.5px] text-slate-400"
            >
              Bu dosya henüz hiçbir üründe kullanılmıyor.
            </p>
          </div>

          <!-- Silme uyarısı -->
          <div
            v-if="drawer.usage?.count"
            class="flex gap-2.5 rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-900 dark:bg-yellow-900/20"
          >
            <span
              class="i-lucide-alert-triangle size-4 shrink-0 text-yellow-600"
            />
            <p class="text-[12px] text-yellow-800 dark:text-yellow-300">
              Bu dosya <b>{{ drawer.usage.count }} yerde</b> kullanılıyor.
              Silersen ilgili alanlarda görsel bozulabilir.
            </p>
          </div>

          <!-- Benzer / kopya -->
          <div v-if="drawer.similar?.length">
            <div
              class="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400"
            >
              Olası Kopya
            </div>
            <div class="grid grid-cols-4 gap-1.5">
              <div
                v-for="s in drawer.similar"
                :key="s.id"
                class="relative aspect-square overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700"
              >
                <img
                  v-if="s.url"
                  :src="s.url"
                  class="h-full w-full object-cover"
                />
                <span
                  v-if="s.duplicate"
                  class="absolute right-1 top-1 rounded bg-red-500 px-1 text-[9px] font-bold text-white"
                  >eş</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </aside>
  </div>
</template>
